import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import Avatar from "@mui/material/Avatar";
import RadioGroup from "@mui/material/RadioGroup";
import FormLabel from "@mui/material/FormLabel";
import AvatarUpload from "../../components/AvatarUpload";

function PersonalDetailsForm({ formData, setFormData, member, edit = false }) {
  const [open, setOpen] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [birthdayError, setBirthdayError] = useState("");

  const handleAvatar = () => setOpen(!open);

  useEffect(() => {
    if (edit && member) {
      setFormData({
        ...formData,
        firstName: member.firstName || "",
        lastName: member.lastName || "",
        email: member.email || "",
        cin: member.cin?.toString() || "",
        birthday: member.birthday
          ? new Date(member.birthday).toLocaleDateString("en-GB") // Format to dd-mm-yyyy
          : "",
        phoneNumber: member.phoneNumber?.toString() || "",
        gender: member.gender || "", // Save M or F from the database
        avatar: member.avatar || "",
        squareCustomerId: member.squareCustomerId || null,
        address: {
          addressLine1: member.address?.addressLine1 || "",
          addressLine2: member.address?.addressLine2 || "",
          city: member.address?.city || "",
          state: member.address?.state || "",
          postalCode: member.address?.postalCode || "",
        },
      });
    }
  }, [edit, member, setFormData]);
  

  const handlePhoneNumberChange = (value) => {
    const phone = value.replace(/[^0-9]/g, ""); // Allow only digits
    if (phone.length < 10 || phone.length > 10) {
      setPhoneError("Phone number must be 10 digits including area code.");
    } else {
      setPhoneError("");
    }
    setFormData({ ...formData, phoneNumber: phone });
  };

  const handleBirthdayChange = (value) => {
    const formattedValue = value
      .replace(/[^0-9]/g, "") // Remove non-digit characters
      .replace(/^(\d{2})(\d{2})/, "$1/$2/") // Insert slashes for day and month
      .replace(/\/(\d{5,})$/, "/$1"); // Prevent more than 4 digits for year
  
    if (formattedValue.length > 10) {
      setBirthdayError("Invalid date format. Use DD/MM/YYYY.");
    } else {
      const [day, month, year] = formattedValue.split("/");
      if (
        formattedValue.match(/^\d{2}\/\d{2}\/\d{4}$/) &&
        Number.isNaN(new Date(`${year}-${month}-${day}`).getTime())
      ) {
        setBirthdayError("Invalid date.");
      } else {
        setBirthdayError("");
      }
    }
  
    setFormData({ ...formData, birthday: formattedValue });
  };

  const formatPhoneNumberForRender = (phoneNumber) => {
    if (phoneNumber.startsWith("+61")) {
      return `0${phoneNumber.slice(3)}`;
    }
    return phoneNumber;
  };
  
  

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={2}>
        {/* First Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            required
            label="First Name"
            name="firstName"
            fullWidth
            size="small"
            value={formData.firstName || ""}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            helperText="Please enter the member's first name"
          />
        </Grid>

        {/* Last Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            required
            label="Last Name"
            name="lastName"
            fullWidth
            size="small"
            value={formData.lastName || ""}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            helperText="Please enter the member's last name"
          />
        </Grid>

        {/* Birthday */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Date of Birth (DD-MM-YYYY)"
            name="birthday"
            fullWidth
            size="small"
            value={formData.birthday || ""}
            onChange={(e) => handleBirthdayChange(e.target.value)}
            error={!!birthdayError}
            helperText={birthdayError || "Enter birthday in DD-MM-YYYY format."}
            inputProps={{
              maxLength: 10,
              placeholder: "DD-MM-YYYY",
            }}
          />
        </Grid>

        {/* Phone Number */}
        <Grid item xs={12} sm={6}>
          <TextField
            required
            label="Phone Number"
            name="phoneNumber"
            fullWidth
            size="small"
            value={formatPhoneNumberForRender(formData.phoneNumber) || ""}
            onChange={(e) => handlePhoneNumberChange(e.target.value)}
            helperText={phoneError || "Enter a 10 digit phone number starting with 0"}
            error={!!phoneError}
            inputProps={{ maxLength: 10 }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            label="Email"
            name="email"
            fullWidth
            size="small"
            value={formData.email || ""}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            helperText="Please enter the member's email"
          />
        </Grid>

        {/* Address Fields */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Address
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Address Line 1"
            fullWidth
            size="small"
            value={formData.address.addressLine1 || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                address: { ...formData.address, addressLine1: e.target.value },
              })
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Address Line 2"
            fullWidth
            size="small"
            value={formData.address.addressLine2 || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                address: { ...formData.address, addressLine2: e.target.value },
              })
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="City"
            fullWidth
            size="small"
            value={formData.address.city || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                address: { ...formData.address, city: e.target.value },
              })
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="State"
            fullWidth
            size="small"
            value={formData.address.state || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                address: { ...formData.address, state: e.target.value },
              })
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Postal Code"
            fullWidth
            size="small"
            value={formData.address.postalCode || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                address: { ...formData.address, postalCode: e.target.value },
              })
            }
          />
        </Grid>

        {/* Gender */}
        <Grid item xs={12} sm={6}>
          <FormLabel>Gender</FormLabel>
          <RadioGroup
            row
            value={formData.gender}
            onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
          >
            <FormControlLabel value="M" control={<Radio />} label="Male" />
            <FormControlLabel value="F" control={<Radio />} label="Female" />
          </RadioGroup>
        </Grid>

        {/* Avatar Upload */}
        <Grid item xs={12} sm={6}>
          <FormLabel>Avatar</FormLabel>
          <Avatar
            sx={{ height: "70px", width: "70px", mb: 1 }}
            alt="avatar"
            src={formData.avatar || "/static/icons/ic_user.png"}
          />
          <Button type="button" variant="contained" onClick={handleAvatar}>
            Upload Avatar
          </Button>
          <AvatarUpload
            isOpen={open}
            toggle={handleAvatar}
            formData={formData}
            setFormData={setFormData}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

PersonalDetailsForm.propTypes = {
  formData: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    cin: PropTypes.string,
    birthday: PropTypes.string, // Ensure birthday is a string
    phoneNumber: PropTypes.string,
    gender: PropTypes.string,
    avatar: PropTypes.string,
    address: PropTypes.shape({
      addressLine1: PropTypes.string,
      addressLine2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      postalCode: PropTypes.string,
    }),
  }),
  setFormData: PropTypes.func.isRequired,
  member: PropTypes.object,
  edit: PropTypes.bool,
};

export default PersonalDetailsForm;
